import { BasicUserAccount } from './account';
import { Place } from './address';
import { DataItem } from './common';
import { CompletedAction, CompletedActionResponse } from './completed-action';
import { Coupon, CouponResponseData } from './coupon';
import { Discount } from './discount';
import { Lead } from './lead';
import { Subpartner } from './partners';
import { ApiResponseBase } from './responses';
import { Source } from './sources';
import { Task } from './task';
import { TaxCode } from './taxCode';
import { TaxRate } from './taxRate';

export enum EstimateStatusIdEnum {
  NewLead = 1,
  EstimateScheduled = 2,
  EstimateCompleted = 3,
  BookedJob = 4,
  Lost = 5,
  Cancelled = 7,
}

export enum JobStatusIdEnum {
  WaitingForApproval = 1,
  Scheduled = 2,
  Completed = 3,
  Closed = 4,
  Cancelled = 5,
  BookedJob = 6,
  Lost = 7,
}

export enum AppointmentCategoryEnum {
  JunkRemoval = 1,
  Labor = 4,
  Moving = 5,
  ExpressJunk = 8,
  MoveLabor = 7,
}

export const categoryNames = {
  [AppointmentCategoryEnum.JunkRemoval]: 'Junk Removal',
  [AppointmentCategoryEnum.Labor]: 'Labor',
  [AppointmentCategoryEnum.Moving]: 'Moving',
  [AppointmentCategoryEnum.MoveLabor]: 'Move Labor',
  [AppointmentCategoryEnum.ExpressJunk]: 'Express Junk',
};

export type AppointmentType = 'ALL' | 'JOB' | 'EST' | 'LJOB';

export const AppointmentTypes: Record<AppointmentType, string> = {
  ALL: 'All types',
  JOB: 'Job',
  EST: 'Estimate',
  LJOB: 'Linked',
};

// APPOINTMENT RESPONSES
export type AppointmentResponse = {
  appointments: AppointmentResponseData[];
} & ApiResponseBase;

export type AppointmentInvoice = {
  id: number;
};

export type AppointmentResponseData = {
  account: BasicUserAccount;
  administrative_fee_enabled: number;
  administrative_fee: number;
  amount_paid: number;
  appointment_tax_total: number;
  balance_due: number;
  category: DataItem;
  client_confirmed: boolean;
  completedactions: CompletedActionResponse[];
  converted_appointment_id: number;
  coupon_total: number;
  coupon: CouponResponseData;
  created: DataItem & {
    date: string;
    image_url?: string;
  };
  destination: Place;
  discount_total: number;
  discount: Discount;
  end_date: string;
  est_status: DataItem;
  estimator_user: {
    id: string;
    name: string;
  };
  grand_total: number;
  id: number;
  invoice: AppointmentInvoice;
  linked: number[];
  location: DataItem & {
    status: DataItem;
    tax_codes: TaxCode[];
    time_zone?: {
      id: number;
      name: string;
      short_name: string;
      offset: string;
    };
  };
  lead?: {
    id: number;
  };
  leads: {
    id: number;
    target_date: string;
    is_qualified: boolean;
    account: {
      id: number;
    };
    location: DataItem;
    category: DataItem;
    noSaleReason: DataItem;
    estimatedValue: {
      low: number;
      high: number;
    };
    task: Task[];
    websiteSession: {
      id: string;
      source: string;
      medium: string;
      content: string;
      campaign: string;
      keyword: string;
      gclid: string;
      google_id: string;
      msclkid: string;
      created_date: string;
    };
    created: {
      id: number;
      name: string;
      date: string;
    };
  }[];
  lost_reason?: DataItem;
  medium?: {
    id: number;
  };
  meta: {
    number_of_kids: number | 'unknown';
    number_of_pets: number | 'unknown';
  };
  net: number;
  notes: {
    invoice: string;
    location_of_junk: string;
    moving_factors: string;
    special_instructions: string;
    type_of_junk: string;
  };
  num_hunks: number;
  origin: Place;
  original_date: string;
  parent: {
    id: number;
  };
  partner?: {
    id: number;
  };
  payments: AppointmentPaymentResponseData[];
  products: AppointmentProductResponseData[];
  product_total: number;
  product_services_total: number;
  pricing: AppointmentPricingResponseData[];
  punctuality_status?: string;
  purchase_order: string;
  quickbooks: AppointmentQuickbooksResponseData;
  quotes: QuoteModel[];
  refund_days: number;
  allow_auto_refund?: boolean;
  met_url?: string;
  junk_url?: string;
  schedules: AppointmentScheduleResponseData[];
  source: Source;
  start_date: string;
  target_date: string;
  status: DataItem;
  stops?: Place[];
  sub_total: number;
  subpartner: Subpartner;
  team: AppointmentTeamResponseData;
  tip_amount: number;
  type: keyof typeof AppointmentTypes;
  tax_rate: TaxRate;
  zone: DataItem;
  royalty: { id: number; is_submitted: boolean };
  tasks: Task[];
  task_comments: Comment[];
  modified?: {
    id: number;
    name: string;
    image_url?: string;
    date: string;
  };
};

// TODO replace this type with GetQuoteAction.Quote
// GetQuoteAction.Quote needs to be extracted to the shared layer
export type QuoteModel = {
  created: DataItem & {
    datetime: string;
    image_url: string;
  };
  id: number;
  notes: {
    move_factors: string;
    special_instructions: string;
  };
  location: DataItem;
  category: DataItem;
  num_hunks: number;
  quote_logs: {
    content: string;
    datetime: string;
    event: string;
    to: string;
  }[];
  type: DataItem;
  met_url?: string;
  based_on: number;
};

export type TaskComment = Comment & {
  taskId: number;
};

export type Appointment = {
  account: BasicUserAccount;
  administrativeFeeEnabled: number;
  administrativeFee: number;
  amountPaid: number;
  appointmentTaxTotal: number;
  balanceDue: number;
  clientConfirmed: boolean;
  category: DataItem;
  completedActions: CompletedAction[];
  convertedAppointmentId: number;
  couponTotal: number;
  coupon: Coupon;
  created: DataItem & {
    date: Date;
  };
  destination: Place;
  discountTotal: number;
  discount: Discount;
  endDate: string;
  estStatus: DataItem;
  estimatorUser: DataItem;
  grandTotal: number;
  id: number;
  invoice: AppointmentInvoice;
  linked: number[];
  location: DataItem & {
    status: DataItem;
    tax_codes: TaxCode[];
  };
  leads: Lead[];
  meta: {
    numberOfKids: number;
    numberOfPets: number;
  };
  net: number;
  notes: {
    invoice: string;
    location_of_junk: string;
    moving_factors: string;
    special_instructions: string;
    type_of_junk: string;
    typeOfJunk?: string;
  };
  numHunks: number;
  origin: Place;
  originalDate: Date;
  purchaseOrder: string;
  quickbooks: AppointmentQuickbooksResponseData;
  startDate: string;
  targetDate: string;
  taxRate: TaxRate;
  type: keyof typeof AppointmentTypes;
  productServicesTotal: number;
  productTotal: number;
  quotes: QuoteModel[];
  refundDays: number;
  allowAutoRefund?: boolean;
  metUrl?: string;
  junkUrl?: string;
  punctualityStatus?: string;
  status: DataItem;
  stops?: Place[];
  subTotal: number;
  team: AppointmentTeamResponseData;
  tipAmount: number;
  schedules: AppointmentScheduleResponseData[];
  source: Source;
  subpartner: Subpartner;
  parent: {
    id: number;
  };
  products: AppointmentProductResponseData[];
  payments: AppointmentPaymentResponseData[];
  pricing: AppointmentPricingResponseData[];
  zone: DataItem;
  royalty: { id: number; isSubmitted: boolean };
  tasks: Task[];
  taskComments: TaskComment[];
  resource?: DataItem;
  websiteSession?: {
    id: string;
  };
};

export type AppointmentTeamResponseData = DataItem & {
  captain: DataItem & {
    image_url?: string;
  };
};

export type AppointmentScheduleTeamResponseData = DataItem & {
  captain: {
    id: number;
    first_name: string;
    last_name: string;
    image_url: string;
  };
  users: {
    id: number;
    first_name: string;
    last_name: string;
    image_url: string;
    title: string;
  }[];
};

export type AppointmentScheduleResponseData = {
  id: number;
  start_date: string;
  end_date: string;
  resource: DataItem;
  development: DataItem;
  team: AppointmentScheduleTeamResponseData;
  colors: {
    background: string;
    foreground: string;
  };
};

export type AppointmentProductResponseData = {
  product_line_id: number;
  product_id: number;
  product_name: string;
  product_subcategory_name: string;
  actual_price: number;
  retail_price: number;
  quantity: number;
  total: number;
  sales_tax_id: number;
  notes: string;
};

export type AppointmentPaymentResponseData = {
  id: number;
  account_id: number;
  location_id: number;
  appointment_id: number;
  payment: {
    amount: number;
    auth_code: string;
    batch: string;
    check_number: string;
    credit_card: {
      address: string;
      name: string;
      number: string;
      type_id: string;
      zip: string;
      expiry_month: number;
      expiry_year: number;
      expiry: number;
    };
    created_date: string;
    date: string;
    memo: string;
    transaction_id: string;
    method: DataItem;
    created_by: string;
    zone: DataItem;
    is_refunded?: boolean;
  };
};

export type AppointmentPricingResponseData = {
  id: number;
  name: string;
  subcategory: DataItem & {
    category: DataItem;
  };
  icon: string;
  price: number;
  active: 0 | 1;
  tax: DataItem & {
    is_taxable: 0 | 1;
  };
};

export type AppointmentQuickbooksResponseData = {
  added_date: string;
  invoice_id: number;
};

export type AppointmentTaxCodeResponseData = DataItem & {
  is_taxable: boolean;
};
